import { createFileRoute, redirect, useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { get_document_template, render_document_template, update_document_template } from '@/api/document-templates'
import type { DocumentTemplate } from '@/types/document-template'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/authStore'
import { FileText, Loader2, CheckCircle2, Edit, Save, X } from 'lucide-react'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

export const Route = createFileRoute('/_authenticated/super-admin-document-template/$slug')({
  beforeLoad: async () => {
    const auth = useAuthStore.getState().auth
    if (!auth.accessToken || !auth.user) {
      throw redirect({ to: '/auth/login' })
    }
    if (auth.user.role !== 'Super Admin' && auth.user.role_name !== 'Super Admin') {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: Page,
})

function Page() {
  const { t, i18n } = useTranslation()
  const { slug } = useParams({ from: '/_authenticated/super-admin-document-template/$slug' })
  const { toast } = useToast()
  const [tpl, setTpl] = useState<DocumentTemplate | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [payload, setPayload] = useState<Record<string, any>>({})
  const [rendering, setRendering] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editData, setEditData] = useState({
    title: '',
    is_active: false,
    html_body: '',
  })
  const quillRef = useRef<Quill | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const isArabic = i18n.language === 'ar'

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await get_document_template(slug)
      setTpl(data)
      setEditData({
        title: data.title,
        is_active: data.is_active,
        html_body: data.engine === 'html' ? (data.html_body || '') : '',
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to load template')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [slug])

  // Initialize Quill editor for editing
  useEffect(() => {
    if (isEditMode && (tpl?.engine === 'html' || tpl?.engine === 'word') && editorContainerRef.current && !quillRef.current) {
      const quill = new Quill(editorContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': 1 }, { 'header': 2 }],
            ['link', 'image'],
            [{ 'direction': 'rtl' }],
            ['clean']
          ]
        },
        placeholder: t('enter_template_content') || 'Enter template content...'
      })

      quill.root.innerHTML = editData.html_body
      quillRef.current = quill

      // Handle text change
      quill.on('text-change', () => {
        const html = quill.root.innerHTML
        setEditData(prev => ({ ...prev, html_body: html }))
      })
    }

    return () => {
      // Clean up only if we're leaving edit mode
      if (!isEditMode && quillRef.current) {
        quillRef.current = null
      }
    }
  }, [isEditMode, tpl])

  useEffect(() => { load() }, [slug])

  const fields = useMemo(() => tpl?.form_fields ?? [], [tpl])

  const onFieldChange = (path: string, value: any) => {
    setPayload((old) => ({
      ...old,
      [path]: value
    }))
  }

  const getFieldValue = (path: string) => {
    return payload[path] ?? ''
  }

  const getPlaceholder = (field: any) => {
    // Use example if available, otherwise generate based on type
    if (field.example) return field.example

    const type = field.type || 'string'
    const format = field.format

    if (format === 'date') return 'YYYY-MM-DD'
    if (format === 'email') return 'exemple@email.com'
    if (format === 'phone') return '+212 6XX XXX XXX'
    if (type === 'number') return '0'
    if (type === 'boolean') return ''

    return `${t('enter') || 'Enter'} ${field.title || field.path}`
  }

  const getInputType = (field: any) => {
    const format = field.format
    const type = field.type || 'string'

    if (format === 'date') return 'date'
    if (format === 'datetime') return 'datetime-local'
    if (format === 'email') return 'email'
    if (format === 'phone' || format === 'tel') return 'tel'
    if (type === 'number' || format === 'number') return 'number'

    return 'text'
  }

  /**
   * Converts payload values to their correct types based on field definitions
   * This ensures the backend receives properly typed data:
   * - "100" (string) → 100 (number) for number fields
   * - "true" (string) → true (boolean) for boolean fields
   * - etc.
   */
  const convertPayloadToCorrectTypes = (payload: Record<string, any>) => {
    const convertedPayload: Record<string, any> = {}

    fields.forEach((field) => {
      const value = payload[field.path]

      // Skip if value is undefined or null or empty string
      if (value === undefined || value === null || value === '') {
        return
      }

      const type = field.type || 'string'
      const format = field.format

      // Convert based on type
      if (type === 'number' || format === 'number') {
        // Convert to number
        const numValue = Number(value)
        if (!isNaN(numValue)) {
          convertedPayload[field.path] = numValue
        } else {
          // If conversion fails, keep original value (backend will validate)
          convertedPayload[field.path] = value
        }
      } else if (type === 'integer') {
        // Convert to integer
        const intValue = parseInt(String(value), 10)
        if (!isNaN(intValue)) {
          convertedPayload[field.path] = intValue
        } else {
          convertedPayload[field.path] = value
        }
      } else if (type === 'boolean') {
        // Convert to boolean - handle various input formats
        if (typeof value === 'boolean') {
          convertedPayload[field.path] = value
        } else if (typeof value === 'string') {
          convertedPayload[field.path] = value.toLowerCase() === 'true' || value === '1'
        } else {
          convertedPayload[field.path] = Boolean(value)
        }
      } else if (type === 'array') {
        // Ensure it's an array
        convertedPayload[field.path] = Array.isArray(value) ? value : [value]
      } else {
        // Keep as string or original value (including dates which are already in correct format)
        convertedPayload[field.path] = String(value)
      }
    })

    return convertedPayload
  }

  const handleSaveTemplate = async () => {
    if (!editData.title.trim()) {
      toast({
        title: t('error') || 'Error',
        description: t('title_required') || 'Title is required',
        variant: 'destructive',
      })
      return
    }

    setIsSaving(true)
    try {
      const updatePayload: any = {
        title: editData.title,
        is_active: editData.is_active,
      }

      // Include html_body if editing HTML template
      if (tpl?.engine === 'html' && editData.html_body) {
        updatePayload.html_body = editData.html_body
      }

      const updated = await update_document_template(tpl!.slug, updatePayload)
      setTpl(updated)
      setIsEditMode(false)

      toast({
        title: t('template_updated') || 'Template Updated',
        description: t('template_saved_successfully') || 'Template has been saved successfully',
      })
    } catch (e: any) {
      toast({
        title: t('error_updating_template') || 'Error Updating Template',
        description: e?.message || String(e),
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const doRender = async () => {
    if (!tpl?.slug) return
    setRendering(true)
    try {
      // Convert payload values to correct types based on field definitions
      const typedPayload = convertPayloadToCorrectTypes(payload)

      console.log('[TemplateRender] Original payload:', payload)
      console.log('[TemplateRender] Typed payload:', typedPayload)
      console.log('[TemplateRender] Field definitions:', fields.map(f => ({ path: f.path, type: f.type, format: f.format })))

      const res = await render_document_template(tpl.slug, typedPayload)
      toast({
        title: t('render_success') || 'Document generated successfully',
        description: t('opening_pdf') || 'Opening PDF in new tab...'
      })
      if (res?.pdf_url) {
        window.open(res.pdf_url, '_blank')
      }
    } catch (e: any) {
      toast({
        title: t('render_failed') || 'Failed to generate document',
        description: e?.message || String(e),
        variant: 'destructive'
      })
    } finally {
      setRendering(false)
    }
  }

  return (
    <>

      <Main>
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='text-center space-y-3'>
              <Loader2 className='w-8 h-8 animate-spin mx-auto text-primary' />
              <p className='text-muted-foreground'>{t('loading') || 'Loading'}...</p>
            </div>
          </div>
        ) : error ? (
          <div className='py-12 text-center'>
            <div className='inline-flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10 mb-4'>
              <FileText className='w-6 h-6 text-destructive' />
            </div>
            <h3 className='text-lg font-semibold mb-2'>{t('error') || 'Error'}</h3>
            <p className='text-muted-foreground'>{error}</p>
          </div>
        ) : !tpl ? (
          <div className='py-12 text-center'>
            <FileText className='w-12 h-12 mx-auto text-muted-foreground/50 mb-4' />
            <p className='text-muted-foreground'>{t('no_results') || 'Template not found'}</p>
          </div>
        ) : (
          <div className='max-w-4xl mx-auto space-y-6'>
            {/* Template Header */}
            <div className='space-y-3'>
              {isEditMode ? (
                <div className='space-y-6'>
                  {/* Basic Fields */}
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='title'>
                        {t('title') || 'Title'} <span className='text-destructive'>*</span>
                      </Label>
                      <Input
                        id='title'
                        value={editData.title}
                        onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder={t('enter_template_title') || 'Template Title'}
                        disabled={isSaving}
                      />
                    </div>

                    <div className='flex items-center gap-2'>
                      <input
                        id='is_active'
                        type='checkbox'
                        checked={editData.is_active}
                        onChange={(e) => setEditData(prev => ({ ...prev, is_active: e.target.checked }))}
                        disabled={isSaving}
                        className='w-4 h-4 rounded'
                      />
                      <Label htmlFor='is_active' className='font-normal cursor-pointer'>
                        {t('active') || 'Active'}
                      </Label>
                    </div>
                  </div>

                  {/* Editor for all Templates (HTML and Word) */}
                  {(tpl?.engine === 'html' || tpl?.engine === 'word') && (
                    <div className='space-y-2'>
                      <Label>{t('template_content') || 'Template Content'}</Label>
                      <Card className='border'>
                        <CardContent className='p-0'>
                          <div
                            ref={editorContainerRef}
                            className='quill-editor'
                            style={{ minHeight: '400px' }}
                          />
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Save/Cancel Buttons */}
                  <div className='flex gap-2 pt-2'>
                    <Button
                      onClick={handleSaveTemplate}
                      disabled={isSaving}
                      size='sm'
                      className='gap-2'
                    >
                      <Save className='w-4 h-4' />
                      {isSaving ? (t('saving') || 'Saving...') : (t('save_changes') || 'Save Changes')}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsEditMode(false)
                        setEditData({
                          title: tpl.title,
                          is_active: tpl.is_active,
                          html_body: tpl.engine === 'html' ? (tpl.html_body || '') : '',
                        })
                      }}
                      disabled={isSaving}
                      variant='outline'
                      size='sm'
                      className='gap-2'
                    >
                      <X className='w-4 h-4' />
                      {t('cancel') || 'Cancel'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='flex items-start justify-between gap-4'>
                  <div>
                    <h1 className='text-3xl font-bold tracking-tight'>{tpl.title}</h1>
                    <p className='text-muted-foreground mt-1'>
                      {`${t('version') || 'Version'} ${tpl.version} • ${tpl.slug}`}
                    </p>
                  </div>
                  <Button
                    onClick={() => setIsEditMode(true)}
                    variant='outline'
                    size='sm'
                    className='gap-2'
                  >
                    <Edit className='w-4 h-4' />
                    {t('edit') || 'Edit'}
                  </Button>
                </div>
              )}

              <div className='flex flex-wrap items-center gap-2'>
                <Badge
                  variant={tpl.is_active ? 'default' : 'secondary'}
                  className='gap-1'
                >
                  {tpl.is_active && <CheckCircle2 className='w-3 h-3' />}
                  {tpl.is_active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                </Badge>

                {tpl.page_layout && (
                  <Badge variant='outline'>
                    {t('layout') || 'Layout'}: {tpl.page_layout}
                  </Badge>
                )}

                <Badge variant='outline'>
                  {tpl.variables?.total_count || 0} {t('variables') || 'variables'}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Template Preview */}
            {(tpl.preview_url || tpl.preview_pdf_url) && (
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg'>
                    {t('template_preview') || 'Template Preview'}
                  </CardTitle>
                  <CardDescription>
                    {t('template_preview_desc') || 'Preview of the document template'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='grid gap-4 md:grid-cols-2'>
                    {tpl.preview_url && (
                      <div className='space-y-2'>
                        <p className='text-sm font-medium'>{t('image_preview') || 'Image Preview'}</p>
                        <img
                          src={tpl.preview_url}
                          alt={tpl.title}
                          className='w-full rounded-lg border object-cover aspect-[3/4]'
                        />
                      </div>
                    )}
                    {tpl.preview_pdf_url && (
                      <div className='space-y-2'>
                        <p className='text-sm font-medium'>{t('pdf_preview') || 'PDF Preview'}</p>
                        <a
                          href={tpl.preview_pdf_url}
                          target='_blank'
                          rel='noreferrer'
                          className='flex flex-col items-center justify-center aspect-[3/4] rounded-lg border bg-muted hover:bg-muted/80 transition-colors'
                        >
                          <FileText className='w-16 h-16 text-muted-foreground mb-3' />
                          <span className='text-sm font-medium'>{t('open_pdf') || 'Open PDF'}</span>
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Document Form - Only show when not editing */}
            {!isEditMode && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t('fill_document_data') || 'Fill Document Data'}
                  </CardTitle>
                  <CardDescription>
                    {t('fill_document_data_desc') || 'Complete the form below to generate your document'}
                  </CardDescription>
                </CardHeader>
                <CardContent className='space-y-6'>
                  <div className='grid gap-6 sm:grid-cols-2'>
                    {fields.map((field) => {
                      const type = field.type || 'string'
                      const format = field.format
                      const title = field.title || field.path
                      const description = field.description
                      const value = getFieldValue(field.path)
                      const placeholder = getPlaceholder(field)
                      const inputType = getInputType(field)
                      const required = field.required || false
                      const isLongText = format === 'long_text' || format === 'text' || format === 'html' || format === 'richtext'

                      return (
                        <div
                          key={field.path}
                          className={isLongText ? 'sm:col-span-2' : ''}
                        >
                          <div className='space-y-2'>
                            <Label htmlFor={field.path} className='flex items-center gap-2'>
                              {title}
                              {required && <span className='text-destructive'>*</span>}
                            </Label>

                            {description && (
                              <p className='text-xs text-muted-foreground'>
                                {description}
                              </p>
                            )}

                            {type === 'boolean' ? (
                              <div className='flex items-center gap-2 pt-2'>
                                <input
                                  id={field.path}
                                  type='checkbox'
                                  checked={!!value}
                                  onChange={(e) => onFieldChange(field.path, e.target.checked)}
                                  className='w-4 h-4 rounded border-gray-300'
                                />
                                <Label htmlFor={field.path} className='font-normal cursor-pointer'>
                                  {placeholder || title}
                                </Label>
                              </div>
                            ) : isLongText ? (
                              <Textarea
                                id={field.path}
                                value={value || ''}
                                onChange={(e) => onFieldChange(field.path, e.target.value)}
                                placeholder={placeholder}
                                rows={6}
                                className='resize-y'
                                dir={isArabic ? 'rtl' : 'ltr'}
                              />
                            ) : field.enum ? (
                              <select
                                id={field.path}
                                value={value || ''}
                                onChange={(e) => onFieldChange(field.path, e.target.value)}
                                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                                required={required}
                              >
                                <option value=''>{t('select') || 'Select'}...</option>
                                {field.enum.map((option: string) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <Input
                                id={field.path}
                                type={inputType}
                                value={value || ''}
                                onChange={(e) => onFieldChange(field.path, e.target.value)}
                                placeholder={placeholder}
                                required={required}
                                dir={isArabic ? 'rtl' : 'ltr'}
                              />
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <Separator />

                  <div className='flex items-center justify-between gap-4'>
                    <p className='text-sm text-muted-foreground'>
                      {t('generate_document_info') || 'Click the button to generate your PDF document'}
                    </p>

                    <Button
                      type='button'
                      onClick={doRender}
                      disabled={rendering}
                      size='lg'
                      className='min-w-[180px]'
                    >
                      {rendering ? (
                        <>
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                          {t('generating') || 'Generating'}...
                        </>
                      ) : (
                        <>
                          <FileText className='w-4 h-4 mr-2' />
                          {t('generate_pdf') || 'Generate PDF'}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </Main>
    </>
  )
}